import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { ENGINES } from '../mock/mock';
import ArcResults from '../components/ArcResults';
import ConstellationBackdrop from '../components/ConstellationBackdrop';
import ArclipseBrandLockup from '../components/ArclipseBrandLockup';

const RESULT_CACHE_KEY = 'arclipse_constellation_last_result_v1';

function readCachedResult() {
  try {
    const raw = sessionStorage.getItem(RESULT_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function Results() {
  const location = useLocation();
  const navigate = useNavigate();
  const cached = readCachedResult();
  const { result, email, sessionId } = location.state || cached;

  useEffect(() => {
    if (!result) {
      navigate('/', { replace: true });
      return;
    }
    try {
      sessionStorage.setItem(RESULT_CACHE_KEY, JSON.stringify({ result, email, sessionId }));
    } catch {
      // Session storage can be blocked in private modes. The normal route state still works.
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!result) return null;

  const eng = ENGINES[result.top];

  const continueBrainScan = () => {
    navigate('/arc-signal', { state: { email, sessionId } });
  };

  return (
    <div className="relative min-h-screen font-body text-white bg-[#050403] overflow-hidden">
      <ConstellationBackdrop variant="result" accent={eng?.primary || '#e4e4e8'} />

      <div className="relative z-10 px-5 md:px-10 py-6 max-w-[1180px] mx-auto">
        <div className="flex items-center justify-between pb-4 border-b border-white/8 mb-10">
          <Link to="/" className="flex items-center gap-3">
            <ArclipseBrandLockup size="sm" />
          </Link>
          <div className="font-data text-[11px] tracking-[0.18em] uppercase text-white/40">
            ARC signal result. {result.isFull ? '94% accuracy' : `${result.accuracy}% accuracy`}
          </div>
        </div>
      </div>

      <div className="relative z-10 px-5 md:px-10">
        <ArcResults
          result={result}
          onEnter={() => navigate('/')}
          enterLabel="Return to Constellation"
          onContinueDeeper={result.isFull ? null : continueBrainScan}
          readOnly={false}
        />
      </div>

      <div className="relative z-10 px-5 md:px-10 pb-16 max-w-[760px] mx-auto">
        <div className="fade-up">
          <div className="panel-strong p-7 md:p-9 text-center rounded-2xl relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(600px 300px at 50% 110%, ${eng?.primary}18, transparent 60%)` }} />
            <div className="relative z-10">
              <div className="font-data text-[10px] tracking-[0.32em] uppercase mb-3" style={{ color: eng?.primary || '#e4e4e8' }}>
                Constellation review signal
              </div>
              <h2 className="font-head text-[24px] md:text-[30px] text-white mb-3">
                Your ARC signal is complete.
              </h2>
              <p className="text-white/60 text-[14.5px] leading-[1.8] max-w-[520px] mx-auto mb-6">
                The result is recorded to {email || 'your email'} and can now be used as second-stage founder context during Constellation review.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6" style={{ borderColor: `${eng?.primary || '#e4e4e8'}55`, background: `${eng?.primary || '#e4e4e8'}12` }}>
                <CheckCircle2 size={13} style={{ color: eng?.primary || '#e4e4e8' }} />
                <span className="font-data text-[11.5px] tracking-wider text-white/70">Saved for manual review</span>
              </div>
              <Link to="/" className="constellation-cta constellation-cta--wide mx-auto">
                Return to Constellation <ArrowRight size={14} />
              </Link>

              <p className="mt-4 text-[11.5px] text-white/35">
                Completing ARC does not automatically accept or reject an application.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="text-[12px] text-white/30 hover:text-white/55 transition-colors">
            Back to Constellation
          </Link>
        </div>
      </div>

    </div>
  );
}
